import {
  MAX_TIER,
  TIER_PREFIX,
  anchorsHoldingOn,
  packDecor as packOn,
  placedTierOn,
  planPickOn,
  planPlacementOn,
  unpackDecor as unpackOn,
  type PickOutcome,
  type PlacementMap,
  type PlacementPlan,
  type Surface,
} from './placement'

// The Keep — a denomination's hall on the Battle tab. See docs/FORTRESS.md for
// the design of record; the rules that matter most here:
//
//   PRESENCE, NOT QUANTITY. Nothing in a hall is ever counted. A decoration is
//   present or absent — never "×47", never "12 members own this", never a
//   contributor name. One member owning the destrier and ten thousand owning it
//   render identically. Counting would turn the hall into a tally of faction
//   size on a topic where real people have real sore spots.
//
//   THE BUILDING IS SECULAR. A keep, not a chapel: every faction on the board —
//   including agnostic and atheist, which data/denominations promises the same
//   styling with no asterisk — gets the same architecture. All religious
//   character comes from decorations members CHOSE to hang; the app never
//   assigns iconography by tradition, so it can never assign it wrongly.
//
//   NO PLAYER-AUTHORED TEXT. No plaques, no dedications, no "hung by". A
//   denomination is more strangers than a congregation, and the church-page
//   rule (no client writes someone else's page) applies doubly here.
//
//   EVERYTHING IS EARNED IN BATTLES, and only battles. The daily drop, study
//   and reading feed the church economy; the keep is the Battle tab's own
//   ladder. Challenges derive from six lifetime counters, so there is no grant
//   table to keep honest — own a decoration iff the counter cleared its goal.

// ── Anchors ──────────────────────────────────────────────────────────────────
// A fixed set of typed mount points, so the hall's render cost never grows no
// matter how much a player owns, and placement is a loadout rather than a
// canvas.

export type MountKind = 'banner' | 'wall' | 'rafters' | 'table' | 'floor' | 'stable'

export interface AnchorDef {
  id: string
  mount: MountKind
  /** Where the prop lands in the hall's 560x300 viewBox (its ground point). */
  x: number
  y: number
  /** Uniform scale applied to the prop at this anchor. */
  s?: number
}

// Positions are calibrated against the painted hall (public/keep/hall.jpg,
// drawn 1120x625 and shown sliced into this 560x300 viewBox): the big bare
// wall runs x 190..435 between the beams and the table, the hearth owns the
// left, the stable arch the right.
/**
 * Whether the keep's hall is offered anywhere (2026-10-29, the owner's call).
 *
 * The hall was the Battle tab's heaviest surface and its one thing that can
 * always wait, and relics now go straight to the player's church instead of
 * through a decorated hall (store/drops.ts). So the hall is HIDDEN, not
 * removed: the counters still count (`useKeep().track()`), `keep_placements`,
 * `keep_offerings`, every RPC and every `va.keep.*` key are untouched, and the
 * unlock toasts stay quiet rather than being forgotten — re-read on the way
 * back, so turning this on again announces nothing stale. Flipping it to true
 * restores the row, the ladder, the sheet, `?keep=1`, the CPU result's next
 * piece and the toasts, with no migration.
 */
export const KEEP_HALL_VISIBLE = false

export const ANCHORS: AnchorDef[] = [
  { id: 'banner_l', mount: 'banner', x: 215, y: 76 },
  { id: 'banner_r', mount: 'banner', x: 405, y: 76 },
  { id: 'wall_1', mount: 'wall', x: 240, y: 148 },
  { id: 'wall_2', mount: 'wall', x: 310, y: 144 },
  { id: 'wall_3', mount: 'wall', x: 380, y: 148 },
  { id: 'rafters_1', mount: 'rafters', x: 310, y: 28 },
  { id: 'rafters_2', mount: 'rafters', x: 448, y: 54 },
  { id: 'table_1', mount: 'table', x: 250, y: 199 },
  { id: 'table_2', mount: 'table', x: 315, y: 199 },
  { id: 'floor_1', mount: 'floor', x: 160, y: 268 },
  { id: 'floor_2', mount: 'floor', x: 420, y: 284 },
  { id: 'stable_1', mount: 'stable', x: 482, y: 244 },
]

export const anchorById = (id: string): AnchorDef | undefined => ANCHORS.find((a) => a.id === id)
export const anchorsForMount = (mount: MountKind): AnchorDef[] =>
  ANCHORS.filter((a) => a.mount === mount)

// ── Decorations ──────────────────────────────────────────────────────────────
// Drawn, not generated: flat SVG props (components/KeepArt) that read at sheet
// size. Availability is universal — anyone can earn and hang anything. Gating
// the rosary to Catholics would be an identity check, it would break the moment
// somebody switched faction, and it would turn a warm object into a permission
// prompt. A hall looks like its tradition because members CHOSE those objects.

export interface DecorDef {
  id: string
  name: string
  mount: MountKind
  blurb: string
}

export const DECOR: DecorDef[] = [
  { id: 'keep_woven_rug', name: 'Woven Rug', mount: 'floor', blurb: 'The first thing that makes stone feel like home.' },
  { id: 'keep_oil_lamp', name: 'Oil Lamp', mount: 'table', blurb: 'A light kept burning on the long table.' },
  { id: 'keep_kite_shield', name: 'Kite Shield', mount: 'wall', blurb: 'Hung point-down over the hearth.' },
  { id: 'keep_sheaf_banner', name: 'Sheaf Banner', mount: 'banner', blurb: 'Barley on a field of gold.' },
  { id: 'keep_rosary', name: 'Rosary', mount: 'table', blurb: 'Laid gently by the lamp.' },
  { id: 'keep_crossed_spears', name: 'Crossed Spears', mount: 'wall', blurb: 'Practice arms, retired to the wall.' },
  { id: 'keep_open_bible', name: 'Open Bible', mount: 'table', blurb: 'Open on its stand, mid-Psalm.' },
  { id: 'keep_lanterns', name: 'Hanging Lanterns', mount: 'rafters', blurb: 'Warm light from the beams.' },
  { id: 'keep_brazier', name: 'Brazier', mount: 'floor', blurb: 'Coals against the evening cold.' },
  { id: 'keep_barrels', name: 'Barrel Stack', mount: 'floor', blurb: 'Provisions for a long siege of studying.' },
  { id: 'keep_tapestry', name: 'Harvest Tapestry', mount: 'wall', blurb: 'A field at harvest, woven in wool.' },
  { id: 'keep_chess', name: 'Chess Set', mount: 'table', blurb: 'A quieter kind of battle.' },
  { id: 'keep_chandelier', name: 'Iron Chandelier', mount: 'rafters', blurb: 'Wrought iron, a dozen candles.' },
  { id: 'keep_armor_rack', name: 'Armor of God Rack', mount: 'wall', blurb: 'All six pieces, hung as a set (Eph 6).' },
  { id: 'keep_destrier', name: 'Armoured Destrier', mount: 'stable', blurb: 'Barded in the faction’s colors — the grail.' },
]

export const decorById = (id?: string | null): DecorDef | undefined =>
  id ? DECOR.find((d) => d.id === id) : undefined
export const decorForMount = (mount: MountKind): DecorDef[] => DECOR.filter((d) => d.mount === mount)

// ── Challenges ───────────────────────────────────────────────────────────────
// Six lifetime counters, all battles. Every challenge is "counter reaches
// goal"; ownership is DERIVED, never granted, so two devices can't disagree and
// there's nothing to revoke. cpu_won counts a simulation and battle_won counts
// a real match — both live on the Battle tab, which is the one corner of the
// app that's allowed to be competitive.

export type KeepCounter =
  | 'cpu_played'
  | 'cpu_won'
  | 'battle_played'
  | 'battle_won'
  | 'battle_perfect'
  | 'battle_combo'

export type KeepCounters = Record<KeepCounter, number>

export const EMPTY_COUNTERS: KeepCounters = {
  cpu_played: 0,
  cpu_won: 0,
  battle_played: 0,
  battle_won: 0,
  battle_perfect: 0,
  battle_combo: 0,
}

export interface ChallengeDef {
  id: string
  counter: KeepCounter
  goal: number
  text: string
  /** The decoration this challenge unlocks. */
  decor: string
}

// Ordered as a ladder: early rows land in the first session, the destrier is a
// season of evenings. Texts say the goal plainly — no shame, no timer, nothing
// expires, and progress only ever goes up.
export const CHALLENGES: ChallengeDef[] = [
  { id: 'k_rug', counter: 'cpu_played', goal: 1, text: 'Race the CPU once', decor: 'keep_woven_rug' },
  { id: 'k_lamp', counter: 'cpu_won', goal: 1, text: 'Beat the CPU once', decor: 'keep_oil_lamp' },
  { id: 'k_shield', counter: 'cpu_won', goal: 3, text: 'Beat the CPU 3 times', decor: 'keep_kite_shield' },
  { id: 'k_sheaf', counter: 'battle_played', goal: 1, text: 'Play a battle with a friend', decor: 'keep_sheaf_banner' },
  { id: 'k_rosary', counter: 'cpu_played', goal: 5, text: 'Race the CPU 5 times', decor: 'keep_rosary' },
  { id: 'k_spears', counter: 'cpu_won', goal: 5, text: 'Beat the CPU 5 times', decor: 'keep_crossed_spears' },
  { id: 'k_bible', counter: 'battle_perfect', goal: 1, text: 'Finish a battle run with no misses', decor: 'keep_open_bible' },
  { id: 'k_lanterns', counter: 'battle_combo', goal: 1, text: 'Hit a 4× combo in a battle', decor: 'keep_lanterns' },
  { id: 'k_brazier', counter: 'cpu_won', goal: 10, text: 'Beat the CPU 10 times', decor: 'keep_brazier' },
  { id: 'k_barrels', counter: 'battle_played', goal: 5, text: 'Play 5 battles with friends', decor: 'keep_barrels' },
  { id: 'k_tapestry', counter: 'battle_won', goal: 3, text: 'Win 3 battles', decor: 'keep_tapestry' },
  { id: 'k_chess', counter: 'battle_perfect', goal: 3, text: '3 battle runs with no misses', decor: 'keep_chess' },
  { id: 'k_chandelier', counter: 'cpu_won', goal: 25, text: 'Beat the CPU 25 times', decor: 'keep_chandelier' },
  { id: 'k_rack', counter: 'battle_won', goal: 8, text: 'Win 8 battles', decor: 'keep_armor_rack' },
  { id: 'k_destrier', counter: 'battle_won', goal: 15, text: 'Win 15 battles', decor: 'keep_destrier' },
]

// ── Tiers as their own unlocks ───────────────────────────────────────────────
// Fine and Grand used to be made by stacking duplicates. They are their own
// rungs on the same ladder now: the same counter, at 2.5x and 5x the goal, so a
// finer piece is still EARNED BY PLAYING and ownership stays a pure function of
// the counters — nothing granted, nothing to revoke, nothing a second device
// can disagree about. The multipliers are deliberately steep enough that Grand
// keeps meaning something (the destrier's Grand is 75 battle wins) without any
// rung expiring or ranking anybody.
//
// The church offering is untouched by construction: its gate has always been
// "placed at Grand", read off the tier suffix in the placement value, and the
// suffix is written exactly as before — only the way a player REACHES Grand
// changed. 0062's ladder, cap and values did not move.

const TIER_GOAL_MULT = [1, 2.5, 5] as const

/** The counter goal for a decoration at a tier (tier 1 is the base goal). */
export function tierGoal(base: number, tier: number): number {
  return Math.ceil(base * (TIER_GOAL_MULT[Math.min(MAX_TIER, Math.max(1, tier)) - 1] ?? 1))
}

/** Ownership is a pure function of the counters. */
export function decorOwned(decorId: string, counters: KeepCounters): boolean {
  return decorTierOwned(decorId, 1, counters)
}

export function decorTierOwned(decorId: string, tier: number, counters: KeepCounters): boolean {
  const ch = CHALLENGES.find((c) => c.decor === decorId)
  if (!ch) return false
  return (counters[ch.counter] ?? 0) >= tierGoal(ch.goal, tier)
}

/** The finest tier of this decoration the counters have earned, or 0. */
export function bestOwnedTier(decorId: string, counters: KeepCounters): number {
  for (let t = MAX_TIER; t >= 1; t--) if (decorTierOwned(decorId, t, counters)) return t
  return 0
}

/** The next tier the counters have not reached yet, for the shelf's one line. */
export function nextTierInfo(
  decorId: string,
  counters: KeepCounters,
): { name: string; goal: number; have: number } | null {
  const ch = CHALLENGES.find((c) => c.decor === decorId)
  if (!ch) return null
  const best = bestOwnedTier(decorId, counters)
  if (best === 0 || best >= MAX_TIER) return null
  return {
    name: TIER_PREFIX[best].trim(),
    goal: tierGoal(ch.goal, best + 1),
    have: counters[ch.counter] ?? 0,
  }
}

export function ownedDecor(counters: KeepCounters): string[] {
  return CHALLENGES.filter((c) => (counters[c.counter] ?? 0) >= c.goal).map((c) => c.decor)
}

// ── The hall's level ─────────────────────────────────────────────────────────
// A faction's hall grows with its POOLED battle wins — earned by playing, and
// nothing buys it (the same split as church levels vs church skins). The curve
// is sub-linear so a huge faction doesn't lap a small one forever; the level is
// shown as a label, never as a rank between factions.

export function keepLevelForWins(wins: number): number {
  return 1 + Math.floor(Math.sqrt(Math.max(0, wins) / 3))
}

export const KEEP_LEVEL_NAMES = [
  'Hall of Timber',
  'Hall of Stone',
  'Walled Keep',
  'High Keep',
  'Great Keep',
  'Bastion',
] as const

export function keepLevelName(level: number): string {
  return KEEP_LEVEL_NAMES[Math.min(KEEP_LEVEL_NAMES.length - 1, Math.max(0, level - 1))]
}

/** 0-based index into the six halls — the tier the room is actually drawn at. */
export function keepTier(level: number): number {
  return Math.min(KEEP_LEVEL_NAMES.length - 1, Math.max(0, Math.floor(level) - 1))
}

/** Wins the faction needs for the next hall, or null at the top. */
export function winsForTier(tier: number): number | null {
  if (tier >= KEEP_LEVEL_NAMES.length - 1) return null
  // Inverse of keepLevelForWins: level L starts at 3(L-1)^2 wins.
  return 3 * (tier + 1) * (tier + 1)
}

// ── Offering a finished piece ────────────────────────────────────────────────
// A decoration merged all the way to Grand has nowhere left to go, so it can be
// given to your church: the Grand one leaves the hall, your church banks the
// points, and you keep the plain decoration (you never stopped owning it — the
// counters did not move).
//
// ONCE EVER PER DECORATION, and the values are small on purpose. Church XP is
// the one number here that ranks anything, and keep counters are clamped rather
// than verified (0059's doctrine: an inflated counter is worth wall furniture,
// not standing). Turning counters into church XP moves them a little closer to
// standing than that doctrine likes, so the whole set is worth 3,100 points —
// under three church levels at the very bottom of the curve — and the
// primary key on (user, decor) is what caps it. See 0062.
//
// KEEP IN SYNC with `keep_decor_offer_value` in 0062. The server decides; this
// copy draws the number on the button.

export const OFFER_VALUES: Record<string, number> = {
  keep_woven_rug: 60,
  keep_oil_lamp: 60,
  keep_kite_shield: 90,
  keep_rosary: 90,
  keep_sheaf_banner: 120,
  keep_crossed_spears: 120,
  keep_open_bible: 150,
  keep_lanterns: 150,
  keep_brazier: 200,
  keep_barrels: 200,
  keep_tapestry: 260,
  keep_chess: 260,
  keep_chandelier: 340,
  keep_armor_rack: 400,
  keep_destrier: 600,
}

export function offerValue(decorId?: string | null): number {
  return (decorId && OFFER_VALUES[decorId]) || 0
}

/** Anchors holding a Grand piece — the only ones that can be offered. */
export function offerableAnchors(placements: Record<string, string>): { anchor: string; decor: string }[] {
  return ANCHORS.flatMap((a) => {
    const { id, tier } = unpackOn(placements[a.id])
    return id && tier >= MAX_TIER ? [{ anchor: a.id, decor: id }] : []
  })
}

// ── The planners live in data/placement.ts ──────────────────────────────────
// Placing, merging and moving are the SAME rules in every little world, so they
// are written once (data/placement.ts) and the keep hands them its own anchor
// set. What used to be four copied algorithms is four wrappers; every call site
// below the app still imports these names from here and none of them changed.
//
// The rules themselves are documented at the top of data/placement.ts: nothing
// is ever lost, a tier is a look rather than a count, and a piece only ever
// lands on an anchor of its own mount.

/** The keep, as a surface the shared planners can reason about. The bands are
 *  where each mount's pieces may stand when the player moves them freely —
 *  drawn from the hall's geometry (560x300), so a banner stays on the chimney
 *  line and a rug stays on stone. Tuned by looking at the real hall. */
export const KEEP_SURFACE: Surface = {
  anchors: ANCHORS,
  mountOf: (id) => decorById(id)?.mount,
  bands: {
    banner: { x0: 200, y0: 60, x1: 420, y1: 95 },
    // The back wall, measured off the paintings rather than guessed. Mapping
    // image pixels through the `slice` transform (scale 560/width, then 6.25
    // units cropped off the top and bottom): the flat wall runs x 183..378 at
    // tier 6, where two pillars frame it, and x 172..395 at tier 1, where a
    // timber post frame does — so x 190..380 is the strip that is wall in
    // every tier. The old x1 of 400 was past the wall's right edge in both,
    // which let a tapestry be pushed onto the pillar.
    //
    // The height is the part that was actually wrong. y 125..165 gave a wall
    // piece FORTY units of travel, and the arched windows tiers 4-6 paint on
    // that wall sit at y 79..182 — so every wall piece was pinned across a
    // window with nowhere to go. Above the windows the wall is clear to about
    // y 70 (the vault springs at ~57), and the table top is at y 194. The band
    // clamps a piece's CENTRE and the tallest wall piece is 50 tall, so
    // y 95..168 is exactly the range that keeps the whole of one on stone: at
    // 95 its top lands on y 70, and at 168 its foot stops short of the table.
    //
    // Nothing here dodges the windows for the player — it cannot, since only
    // one clear column (x 244..322) exists between them and three wall anchors
    // will not fit in it. What it does is give back the vertical room to
    // arrange around them, which is the whole of the complaint.
    wall: { x0: 190, y0: 95, x1: 380, y1: 168 },
    rafters: { x0: 150, y0: 20, x1: 460, y1: 70 },
    table: { x0: 235, y0: 192, x1: 330, y1: 206 },
    floor: { x0: 90, y0: 245, x1: 470, y1: 292 },
    stable: { x0: 465, y0: 235, x1: 500, y1: 250 },
  },
}

export const MAX_DECOR_TIER = MAX_TIER

export { packOn as packDecor, unpackOn as unpackDecor }
export type { PickOutcome, PlacementPlan }

/** 'Grand Woven Rug' — the name a tiered decoration wears in the UI. */
export function decorName(value?: string | null): string {
  const { id, tier } = unpackOn(value)
  const def = decorById(id)
  if (!def) return ''
  return `${TIER_PREFIX[tier - 1] ?? ''}${def.name}`
}

export function planPlacement(
  placements: PlacementMap,
  anchor: string,
  decorId: string | null,
): PlacementPlan {
  return planPlacementOn(KEEP_SURFACE, placements, anchor, decorId)
}

export function planPick(placements: PlacementMap, decorId: string, tier = 1): PickOutcome {
  return planPickOn(KEEP_SURFACE, placements, decorId, tier)
}

/** Every anchor currently holding this decoration, best tier first. */
export function anchorsHolding(placements: PlacementMap, decorId: string): string[] {
  return anchorsHoldingOn(KEEP_SURFACE, placements, decorId)
}

/** The best tier of this decoration currently in the hall, or 0 if it's not out. */
export function placedTier(placements: PlacementMap, decorId: string): number {
  return placedTierOn(KEEP_SURFACE, placements, decorId)
}
